import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EmeraldSwitch from "../../components/EmeraldSwitch";
import { listNotificationPreferences, upsertNotificationPreference } from "../../api/notificationPreferences";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";

const DEFAULT_CHANNEL = "EMAIL";

// Staff topics are shown only when the backend lists them for this user: it decides eligibility from the
// user's Lumen permissions (for example reports.new-families:view for FAMILY_NEW).
const DEFAULT_TOPICS = [
  {
    topic: "APPLICATION_NEW",
    gatedByServer: true,
    labelKey: "notifications.preferences.topics.APPLICATION_NEW.label",
    descriptionKey: "notifications.preferences.topics.APPLICATION_NEW.description",
    defaultLabel: "New application submitted",
    defaultDescription: "Receive an email when a new application is submitted.",
  },
  {
    topic: "MUTATION_REQUEST",
    gatedByServer: true,
    labelKey: "notifications.preferences.topics.MUTATION_REQUEST.label",
    descriptionKey: "notifications.preferences.topics.MUTATION_REQUEST.description",
    defaultLabel: "New mutation request submitted",
    defaultDescription: "Receive an email when a new mutation request is submitted.",
  },
  {
    topic: "FAMILY_NEW",
    gatedByServer: true,
    labelKey: "notifications.preferences.topics.FAMILY_NEW.label",
    descriptionKey: "notifications.preferences.topics.FAMILY_NEW.description",
    defaultLabel: "New family registered",
    defaultDescription: "Receive an email when a new family is added. A welcome package may need to be prepared.",
  },
];

const toMap = (items) => {
  if (!Array.isArray(items)) return {};
  return items.reduce((acc, item) => {
    if (item?.topic) acc[item.topic] = item;
    return acc;
  }, {});
};

export default function NotificationPreferencesSection({ topicsConfig = DEFAULT_TOPICS, presentation = "card" }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [savingTopic, setSavingTopic] = useState(null);
  const [prefsMap, setPrefsMap] = useState({});

  const eligibleTopics = useMemo(
    () => (topicsConfig || []).filter((topic) => !topic?.gatedByServer || Boolean(prefsMap?.[topic.topic])),
    [topicsConfig, prefsMap]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchPrefs = async () => {
      setLoading(true);
      setError("");
      try {
        const preferences = await listNotificationPreferences();
        if (!cancelled) {
          setPrefsMap(toMap(preferences));
        }
      } catch (err) {
        if (!cancelled) {
          const mapped = mapHttpErrorToMessage(err, t);
          setError(
            mapped?.message ||
              t("notifications.preferences.load_error", {
                defaultValue: "We could not load your notification preferences.",
              })
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPrefs();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleToggle = async (topicId, enabled) => {
    setSavingTopic(topicId);
    setFlash(null);
    setError("");
    try {
      await upsertNotificationPreference(topicId, { channel: DEFAULT_CHANNEL, enabled });
      setPrefsMap((prev) => ({
        ...prev,
        [topicId]: {
          ...(prev?.[topicId] || {}),
          topic: topicId,
          channel: DEFAULT_CHANNEL,
          enabled,
        },
      }));
      setFlash({
        type: "success",
        message: t("notifications.preferences.save_success", {
          defaultValue: "Your preference was updated.",
        }),
      });
      setTimeout(() => {
        setFlash((prevFlash) => (prevFlash?.type === "success" ? null : prevFlash));
      }, 4000);
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setFlash({
        type: "error",
        message:
          mapped?.message ||
          t("notifications.preferences.save_error", {
            defaultValue: "Saving this preference failed. Please try again.",
          }),
      });
    } finally {
      setSavingTopic(null);
    }
  };

  // Nothing to choose from (or still loading); a load error is still shown.
  if (!eligibleTopics.length && !error) return null;

  const embedded = presentation === "embedded";
  const Container = embedded ? "div" : "section";
  const containerClassName = embedded
    ? "text-slate-950 dark:text-slate-100"
    : "bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 text-gray-900 dark:text-gray-200";
  const titleClassName = embedded
    ? "text-xl font-black tracking-tight text-slate-950 dark:text-slate-50"
    : "text-xl font-semibold";
  const bodyTextClassName = embedded
    ? "mt-1 text-sm font-medium text-slate-500 dark:text-slate-400"
    : "mt-1 text-sm text-gray-600 dark:text-gray-300";
  const dividerClassName = embedded ? "divide-y divide-slate-200/80 dark:divide-slate-800" : "divide-y divide-gray-200 dark:divide-gray-700";
  const itemTitleClassName = embedded
    ? "font-bold text-slate-950 dark:text-slate-100"
    : "font-medium text-gray-900 dark:text-gray-100";
  const itemTextClassName = embedded
    ? "mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300"
    : "mt-1 text-sm text-gray-600 dark:text-gray-300";
  const metaTextClassName = embedded
    ? "mt-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400"
    : "mt-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400";
  const statusTextClassName = embedded
    ? "text-xs font-bold text-slate-500 dark:text-slate-400"
    : "text-xs font-medium text-gray-500 dark:text-gray-400";
  const loadingClassName = embedded
    ? "mt-4 text-sm font-medium text-slate-500 dark:text-slate-400"
    : "mt-4 text-sm text-gray-500 dark:text-gray-400";

  return (
    <Container className={containerClassName}>
      <header className="mb-4">
        <h2 className={titleClassName}>
          {t("notifications.preferences.title", {
            defaultValue: "Notification preferences",
          })}
        </h2>
        <p className={bodyTextClassName}>
          {t("notifications.preferences.description", {
            defaultValue: "Choose which topics should send you email notifications.",
          })}
        </p>
      </header>

      {flash && (
        <div
          className={`mb-4 rounded border px-3 py-2 text-sm ${
            flash.type === "success"
              ? "border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-emerald-400 dark:bg-emerald-500/10 dark:text-emerald-200"
              : "border-red-500 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-500/10 dark:text-red-200"
          }`}
        >
          {flash.message}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded border border-red-500 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-400 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      <ul className={dividerClassName}>
        {eligibleTopics.map(({ topic, labelKey, descriptionKey, defaultLabel, defaultDescription }) => {
          const pref = prefsMap?.[topic];
          const isEnabled = Boolean(pref?.enabled);
          const pending = savingTopic === topic || loading;
          const labelFallback = defaultLabel || topic || "Notification";
          const descriptionFallback = defaultDescription || t("notifications.preferences.default_description", {
            defaultValue: "Receive updates for this topic via email.",
          });
          return (
            <li key={topic} className="py-4 flex items-start justify-between gap-4">
              <div>
                <h3 className={itemTitleClassName}>
                  {t(labelKey, {
                    defaultValue: labelFallback,
                  })}
                </h3>
                <p className={itemTextClassName}>
                  {t(descriptionKey, {
                    defaultValue: descriptionFallback,
                  })}
                </p>
                <p className={metaTextClassName}>
                  {t("notifications.preferences.channel_email", {
                    defaultValue: "Channel: email",
                  })}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <EmeraldSwitch
                  checked={isEnabled}
                  onChange={(value) => handleToggle(topic, value)}
                  disabled={pending}
                />
                <span className={statusTextClassName}>
                  {isEnabled
                    ? t("notifications.preferences.status_on", { defaultValue: "Enabled" })
                    : t("notifications.preferences.status_off", { defaultValue: "Disabled" })}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {loading && (
        <div className={loadingClassName}>
          {t("notifications.preferences.loading", { defaultValue: "Loading preferences..." })}
        </div>
      )}
    </Container>
  );
}
